/*
  Nebula product page (sections/nebula-product.liquid).
  - Tabs: Specifications / Description (ARIA tabs, arrow keys, Home/End). Without JS both panels show.
  - Sticky bar: its button submits the main product form (form attribute), so product-form.js does the
    cart request. This file only mirrors the main button state, shows feedback and reserves page space.
  - Variant change: swaps the sticky price/variant and performance guidance from the section HTML that
    global.js already fetched (PUB_SUB_EVENTS.variantChange). No extra requests.
  - Accessories: updates price/availability text when an option is chosen.
  - Gallery: previous/next arrows, thumbnail strip selection, mouse drag, keyboard, variant
    featured-media sync. No hover zoom.
  - FPS block: game-type dropdown swaps the AAA+ / Esports bars and image (both server-rendered).
  - Sticky bar: "Shipping details" toggle below 990px (animated by nebula-accordion.js), always open on desktop.
  - Desktop: sticky left column (gallery + FPS box).
  Re-initialises cleanly in the theme editor (shopify:section:load / unload).
*/
(() => {
  const LOG = '[nebula-product]';
  const instances = new Map();

  function initTabs(root, signal) {
    root.querySelectorAll('[data-nebula-tabs]').forEach((tabs) => {
      const list = tabs.querySelector('[role="tablist"]');
      if (!list) return;
      const buttons = Array.from(list.querySelectorAll('[role="tab"]'));
      const panels = buttons.map((b) => document.getElementById(b.getAttribute('aria-controls')));
      if (panels.some((p) => !p)) {
        console.warn(LOG, 'tab panel missing', tabs);
        return;
      }

      const select = (index, focus) => {
        buttons.forEach((b, i) => {
          const on = i === index;
          b.setAttribute('aria-selected', String(on));
          b.tabIndex = on ? 0 : -1;
          panels[i].hidden = !on;
        });
        if (focus) buttons[index].focus();
      };

      list.hidden = false;
      tabs.classList.add('is-enhanced');
      select(0, false);

      buttons.forEach((b, i) => b.addEventListener('click', () => select(i, false), { signal }));
      list.addEventListener(
        'keydown',
        (e) => {
          const current = buttons.indexOf(document.activeElement);
          if (current < 0) return;
          let next = null;
          if (e.key === 'ArrowRight') next = (current + 1) % buttons.length;
          else if (e.key === 'ArrowLeft') next = (current - 1 + buttons.length) % buttons.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = buttons.length - 1;
          if (next === null) return;
          e.preventDefault();
          select(next, true);
        },
        { signal }
      );
    });
  }

  function initSticky(root, signal, cleanups) {
    const bar = root.querySelector('[data-nebula-sticky]');
    if (!bar) return;
    const mainButton = document.getElementById(bar.dataset.mainButton);
    const stickyButton = bar.querySelector('[data-sticky-add]');
    const label = bar.querySelector('[data-sticky-label]');
    const msg = bar.querySelector('[data-sticky-msg]');
    if (!mainButton || !stickyButton) {
      console.warn(LOG, 'sticky bar: main ADD TO CART button not found, hiding bar', bar.dataset.mainButton);
      bar.hidden = true;
      return;
    }
    const productForm = mainButton.closest('product-form');
    const errorWrapper = productForm && productForm.querySelector('.product-form__error-message-wrapper');

    // Reserve space at the bottom of the page for the bar's real height.
    document.body.classList.add('nebula-pdp-sticky-on');
    const setHeight = () => document.body.style.setProperty('--nebula-sticky-h', `${bar.offsetHeight}px`);
    setHeight();
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(setHeight);
      ro.observe(bar);
      cleanups.push(() => ro.disconnect());
    }
    cleanups.push(() => {
      document.body.classList.remove('nebula-pdp-sticky-on');
      document.body.style.removeProperty('--nebula-sticky-h');
    });

    // Below 990px the header (z-index 3 on phones) stacks under the bar (20), so while a header menu
    // drawer, search modal / search sidebar or header popover is open the bar is hidden
    // (body.nebula-sticky-covered -> visibility:hidden; page padding stays, so nothing jumps).
    // Only elements that cover the screen on phones/tablets count (desktop mega menus do not):
    //   Dawn's body.overflow-hidden-{mobile,tablet} (menu drawer open), the menu drawer itself
    //   (header-drawer > details[open]), the search modal (details-modal > details[open]),
    //   predictive-search[open], and this theme's custom #mobile-search-sidebar.open.
    // The same selectors are used by the :has() fallback in nebula-product.css; keep them in step. The bar's z-index is not lowered: content and app widgets
    // stay under it while scrolling; the cart drawer and dialogs already sit above it.
    const small = window.matchMedia('(max-width: 989px)');

    // Never sticky over the footer: the bar moves into a holder at the END of #MainContent
    // (.nebula-sticky-host, position: sticky; bottom: 0 in CSS). The browser keeps it on the bottom of
    // the screen while the page scrolls and lets it stop at the end of the main content, right above
    // the theme footer: no per-frame JS positioning, so no lag or jump (also when a phone's address
    // bar shows/hides). The holder carries .nebula-pdp so every existing bar style still applies.
    // The bar is hidden by CSS until it is inside the holder, so nothing flashes on first paint.
    const mainEl = document.getElementById('MainContent') || document.body;
    const home = bar.parentNode;
    const homeNext = bar.nextSibling;
    const host = document.createElement('div');
    host.className = 'nebula-pdp nebula-sticky-host';
    host.setAttribute('data-nebula-sticky-host', '');

    // Phones/tablets (<990px): the bar only appears once the main ADD TO CART button has been
    // scrolled past (its bottom is above the sticky header). Before that: .is-away (slides out).
    let scrollFrame = 0;
    const onScroll = () => {
      scrollFrame = 0;
      let away = false;
      if (small.matches) {
        const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        away = mainButton.getBoundingClientRect().bottom > headerH;
      }
      bar.classList.toggle('is-away', away);
    };
    // Decide the state before the bar becomes visible (same task, before first paint).
    onScroll();
    mainEl.appendChild(host);
    host.appendChild(bar);
    const scheduleScroll = () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(onScroll); };
    window.addEventListener('scroll', scheduleScroll, { passive: true, signal });
    window.addEventListener('resize', scheduleScroll, { signal });
    small.addEventListener('change', scheduleScroll, { signal });
    cleanups.push(() => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      bar.classList.remove('is-away');
      if (home && home.isConnected) home.insertBefore(bar, homeNext && homeNext.parentNode === home ? homeNext : null);
      host.remove();
    });
    const headerEl = document.querySelector('.section-header');
    const searchSidebar = document.getElementById('mobile-search-sidebar');
    const syncCovered = () => {
      const open =
        /\boverflow-hidden-(mobile|tablet)\b/.test(document.body.className) ||
        Boolean(headerEl && headerEl.querySelector('header-drawer > details[open], details-modal > details[open], predictive-search[open]')) ||
        Boolean(searchSidebar && searchSidebar.classList.contains('open'));
      document.body.classList.toggle('nebula-sticky-covered', small.matches && open);
    };
    const coverMo = new MutationObserver(syncCovered);
    coverMo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    if (headerEl) coverMo.observe(headerEl, { attributes: true, attributeFilter: ['open'], subtree: true });
    if (searchSidebar) coverMo.observe(searchSidebar, { attributes: true, attributeFilter: ['class'] });
    small.addEventListener('change', syncCovered, { signal });
    syncCovered();
    cleanups.push(() => {
      coverMo.disconnect();
      document.body.classList.remove('nebula-sticky-covered');
    });

    // Delivery / collection lines: always shown on desktop; behind "Shipping details" below 990px.
    const ship = bar.querySelector('[data-sticky-ship]');
    if (ship) {
      const desktop = window.matchMedia('(min-width: 990px)');
      const syncShip = () => { ship.open = desktop.matches; };
      syncShip();
      desktop.addEventListener('change', syncShip, { signal });
      // Desktop has no toggle: keep it open even if the summary is activated by keyboard.
      ship.addEventListener('toggle', () => { if (desktop.matches && !ship.open) ship.open = true; }, { signal });
    }

    // Development only: Shopify's preview toolbar is fixed to the bottom of preview pages and would
    // cover the bar. When (and only when) that toolbar is present and visible, lift the bar above it.
    // On the live storefront the toolbar does not exist, so the offset stays 0.
    const previewOffset = () => {
      const pb = document.querySelector('#preview-bar-iframe, #PBarNextFrameWrapper');
      let offset = 0;
      if (pb) {
        const r = pb.getBoundingClientRect();
        if (r.height > 0 && r.bottom >= window.innerHeight - 2) offset = Math.max(0, Math.round(window.innerHeight - r.top));
      }
      document.body.style.setProperty('--nebula-preview-offset', offset + 'px');
    };
    previewOffset();
    const previewTimer = setInterval(() => { if (!document.hidden) previewOffset(); }, 2000);
    window.addEventListener('resize', previewOffset, { signal });
    cleanups.push(() => {
      clearInterval(previewTimer);
      document.body.style.removeProperty('--nebula-preview-offset');
    });

    // Mirror the main button: disabled state and label (Add to cart / Sold out / Unavailable).
    let pending = false;
    let msgTimer = null;
    const showMsg = (text, isError) => {
      clearTimeout(msgTimer);
      msg.textContent = text || '';
      msg.classList.toggle('is-error', Boolean(isError));
      if (text && !isError) msgTimer = setTimeout(() => (msg.textContent = ''), 3000);
    };
    const sync = () => {
      const disabled = mainButton.disabled || mainButton.hasAttribute('disabled');
      const loading = mainButton.classList.contains('loading');
      stickyButton.disabled = disabled;
      stickyButton.classList.toggle('loading', loading && pending);
      stickyButton.setAttribute('aria-busy', String(loading && pending));
      const mainLabel = mainButton.querySelector('span:not(.hidden):not(.visually-hidden)');
      if (mainLabel && label) label.textContent = mainLabel.textContent.trim();

      if (pending && !loading) {
        pending = false;
        const errorShown = errorWrapper && !errorWrapper.hasAttribute('hidden');
        if (errorShown) {
          const text = errorWrapper.querySelector('.product-form__error-message');
          showMsg(text ? text.textContent.trim() : 'Could not add to cart.', true);
        } else {
          showMsg('Added to cart', false);
        }
      }
    };
    const mo = new MutationObserver(sync);
    mo.observe(mainButton, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled'], childList: true, subtree: true, characterData: true });
    if (errorWrapper) mo.observe(errorWrapper, { attributes: true, attributeFilter: ['hidden'] });
    cleanups.push(() => mo.disconnect());
    sync();

    // Same guard as the main button: no submit while disabled or a request is running.
    stickyButton.addEventListener(
      'click',
      (e) => {
        if (mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          return;
        }
        showMsg('', false);
        pending = true;
      },
      { signal }
    );
  }

  function initAccessories(root, signal) {
    root.addEventListener(
      'change',
      (e) => {
        const select = e.target.closest('[data-acc-select]');
        if (!select) return;
        const item = select.closest('[data-nebula-acc]');
        const opt = select.selectedOptions[0];
        if (!item || !opt) return;
        const now = item.querySelector('[data-acc-now]');
        const was = item.querySelector('[data-acc-was]');
        const button = item.querySelector('button[name="add"]');
        if (now) now.textContent = opt.dataset.price || '';
        if (was) {
          was.textContent = opt.dataset.compare || '';
          was.hidden = !opt.dataset.compare;
        }
        if (button) button.disabled = opt.disabled;
      },
      { signal }
    );
  }

  function initVariantSync(root, cleanups) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      console.warn(LOG, 'pubsub not available; sticky price will not follow variant changes');
      return;
    }
    const sectionId = root.dataset.section;
    const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (!event || !event.data || event.data.sectionId !== sectionId || !event.data.html) return;
      ['NebulaStickyInfo-', 'NebulaPerf-'].forEach((prefix) => {
        const id = prefix + sectionId;
        const source = event.data.html.getElementById(id);
        const target = document.getElementById(id);
        if (source && target) target.innerHTML = source.innerHTML;
      });
    });
    cleanups.push(unsubscribe);
  }

  // Gallery: thumbnail strip (click, keyboard, mouse drag; touch uses native scrolling so vertical
  // page scrolling is untouched), featured-media selection and variant sync. No lightbox.
  function initGallery(root, signal, cleanups) {
    const gallery = root.querySelector('[data-nebula-gallery]');
    if (!gallery) return;
    const slides = Array.from(gallery.querySelectorAll('.nebula-gallery__slide'));
    const strip = gallery.querySelector('[data-gallery-strip]');
    const thumbs = strip ? Array.from(strip.querySelectorAll('.nebula-gallery__thumb')) : [];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const revealThumb = (thumb) => {
      if (!strip) return;
      const t = thumb.getBoundingClientRect();
      const s = strip.getBoundingClientRect();
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';
      if (t.left < s.left) strip.scrollBy({ left: t.left - s.left - 8, behavior });
      else if (t.right > s.right) strip.scrollBy({ left: t.right - s.right + 8, behavior });
    };

    const select = (mediaId, focus) => {
      const id = String(mediaId);
      const target = slides.find((slide) => slide.dataset.mediaId === id);
      if (!target) return;
      slides.forEach((slide) => {
        const on = slide === target;
        slide.hidden = !on;
        slide.classList.toggle('is-active', on);
        if (!on) slide.querySelectorAll('video').forEach((v) => v.pause());
      });
      gallery.dispatchEvent(new CustomEvent('nebula:media-change'));
      thumbs.forEach((thumb) => {
        if (thumb.dataset.mediaId === id) {
          thumb.setAttribute('aria-current', 'true');
          revealThumb(thumb);
          if (focus) thumb.focus({ preventScroll: true });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    };

    // Previous / next (wrap around, like the live slider). Used by the arrows and by swipe.
    const step = (by) => {
      if (slides.length < 2) return;
      const current = slides.findIndex((slide) => slide.classList.contains('is-active'));
      const next = (Math.max(0, current) + by + slides.length) % slides.length;
      select(slides[next].dataset.mediaId, false);
    };

    let suppressArrowClick = false;
    gallery.addEventListener('click', (e) => {
      const arrow = e.target.closest('[data-gallery-step]');
      if (!arrow) return;
      if (suppressArrowClick) return;
      step(Number(arrow.dataset.galleryStep));
    }, { signal });

    // Swipe on the featured image: touch and pen only (mouse keeps arrows, thumbnails, keyboard).
    // A swipe counts only when horizontal movement is >= 40px and > 1.5x the vertical movement;
    // anything else is left to the browser, so vertical page scrolling is never blocked (CSS
    // touch-action: pan-y pinch-zoom; passive listeners; no preventDefault). Multi-touch cancels
    // (pinch zoom keeps working). Not started on arrows or on video / iframe / 3D model slides.
    const stage = gallery.querySelector('.nebula-gallery__stage');
    if (stage && slides.length > 1) {
      const touches = new Set();
      let start = null;
      stage.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        touches.add(e.pointerId);
        if (touches.size > 1) { start = null; return; }
        const active = slides.find((slide) => slide.classList.contains('is-active'));
        if (e.target.closest('[data-gallery-step]') || (active && active.querySelector('video, iframe, model-viewer'))) return;
        start = { id: e.pointerId, x: e.clientX, y: e.clientY };
      }, { signal, passive: true });
      const end = (e) => {
        touches.delete(e.pointerId);
        if (!start || e.pointerId !== start.id) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        start = null;
        if (e.type !== 'pointerup') return;
        if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          // The click that may follow must not also hit an arrow.
          suppressArrowClick = true;
          setTimeout(() => { suppressArrowClick = false; }, 350);
          step(dx < 0 ? 1 : -1);
        }
      };
      stage.addEventListener('pointerup', end, { signal, passive: true });
      // pointercancel = the browser took over (vertical scroll, pinch): never a swipe.
      stage.addEventListener('pointercancel', end, { signal, passive: true });
    }

    if (strip) {
      let down = false;
      let dragged = false;
      let suppressClick = false;
      let startX = 0;
      let startScroll = 0;
      let pointerId = null;

      strip.addEventListener('click', (e) => {
        if (suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const thumb = e.target.closest('.nebula-gallery__thumb');
        if (thumb) select(thumb.dataset.mediaId, false);
      }, { signal, capture: true });

      strip.addEventListener('keydown', (e) => {
        const i = thumbs.indexOf(document.activeElement);
        if (i < 0) return;
        let next = null;
        if (e.key === 'ArrowRight') next = Math.min(thumbs.length - 1, i + 1);
        else if (e.key === 'ArrowLeft') next = Math.max(0, i - 1);
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = thumbs.length - 1;
        if (next === null) return;
        e.preventDefault();
        select(thumbs[next].dataset.mediaId, true);
      }, { signal });

      // Mouse only: touch and pen keep native overflow scrolling.
      strip.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true;
        dragged = false;
        startX = e.clientX;
        startScroll = strip.scrollLeft;
        pointerId = e.pointerId;
      }, { signal });

      strip.addEventListener('pointermove', (e) => {
        if (!down || e.pointerId !== pointerId) return;
        const dx = e.clientX - startX;
        if (!dragged && Math.abs(dx) > 6) {
          dragged = true;
          strip.classList.add('is-dragging');
          try { strip.setPointerCapture(pointerId); } catch (err) { /* ignore */ }
        }
        if (dragged) strip.scrollLeft = startScroll - dx;
      }, { signal });

      const endDrag = () => {
        if (!down) return;
        down = false;
        if (dragged) {
          // The click that follows a completed drag must not select a thumbnail.
          suppressClick = true;
          setTimeout(() => { suppressClick = false; }, 0);
          strip.classList.remove('is-dragging');
          try { strip.releasePointerCapture(pointerId); } catch (err) { /* ignore */ }
        }
        dragged = false;
      };
      strip.addEventListener('pointerup', endDrag, { signal });
      strip.addEventListener('pointercancel', endDrag, { signal });
      strip.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) endDrag(); }, { signal });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const data = event && event.data;
        if (!data || data.sectionId !== root.dataset.section) return;
        if (data.variant && data.variant.featured_media) select(data.variant.featured_media.id, false);
      });
      cleanups.push(unsubscribe);
    }
  }

  // Desktop left column (gallery + FPS box) sticks while the taller right column scrolls.
  // --nebula-media-top: under the sticky header when the column fits between the header and the
  // purchase bar; otherwise negative, so the column scrolls until its bottom is visible, then sticks.
  function initMediaSticky(root, signal, cleanups) {
    const inner = root.querySelector('[data-nebula-media-sticky]');
    if (!inner) return;
    const desktop = window.matchMedia('(min-width: 990px)');
    const gap = 16;
    let frame = 0;
    const num = (el, name) => parseFloat(getComputedStyle(el).getPropertyValue(name)) || 0;
    const update = () => {
      frame = 0;
      if (!desktop.matches) {
        inner.style.removeProperty('--nebula-media-top');
        return;
      }
      const topAligned = num(document.documentElement, '--header-height') + gap;
      const bottomLimit = window.innerHeight - num(document.body, '--nebula-sticky-h') - num(document.body, '--nebula-preview-offset') - gap;
      const height = inner.offsetHeight;
      const top = topAligned + height > bottomLimit ? bottomLimit - height : topAligned;
      inner.style.setProperty('--nebula-media-top', Math.round(top) + 'px');
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('resize', schedule, { signal });
    desktop.addEventListener('change', schedule, { signal });
    if ('ResizeObserver' in window) {
      // The column (variant swaps, lazy images) and the purchase bar (shipping toggle) change height.
      const ro = new ResizeObserver(schedule);
      ro.observe(inner);
      const bar = root.querySelector('[data-nebula-sticky]');
      if (bar) ro.observe(bar);
      cleanups.push(() => ro.disconnect());
    }
    cleanups.push(() => { if (frame) cancelAnimationFrame(frame); });
  }

  // FPS block: the dropdown shows one game type's bars and image. Delegated on the section root, so
  // it keeps working after a variant change replaces #NebulaPerf-… with fresh HTML.
  function initFps(root, signal) {
    root.addEventListener('change', (e) => {
      const select = e.target.closest('[data-fps-select]');
      if (!select) return;
      const box = select.closest('[data-nebula-fps]');
      if (!box) return;
      box.querySelectorAll('[data-fps-panel]').forEach((p) => { p.hidden = p.dataset.fpsPanel !== select.value; });
      box.querySelectorAll('[data-fps-media]').forEach((m) => { m.hidden = m.dataset.fpsMedia !== select.value; });
    }, { signal });
  }

  // Discount-code copy buttons (e.g. FREESHIP): clipboard API with a textarea fallback, brief "Copied!".
  function initCopy(root, signal) {
    const legacyCopy = (text) => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (err) { /* ignore */ }
      document.body.removeChild(ta);
    };
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-nebula-copy]');
      if (!btn || btn.dataset.busy) return;
      const code = btn.dataset.nebulaCopy;
      const label = btn.querySelector('.nebula-ship__code');
      const done = () => {
        btn.dataset.busy = '1';
        btn.classList.add('is-done');
        if (label) label.textContent = 'Copied!';
        setTimeout(() => {
          btn.classList.remove('is-done');
          if (label) label.textContent = code;
          delete btn.dataset.busy;
        }, 1600);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done, () => { legacyCopy(code); done(); });
      } else {
        legacyCopy(code);
        done();
      }
    }, { signal });
  }

  function init(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const cleanups = [() => controller.abort()];
    initGallery(root, controller.signal, cleanups);
    initCopy(root, controller.signal);
    initFps(root, controller.signal);
    initMediaSticky(root, controller.signal, cleanups);
    initTabs(root, controller.signal);
    initSticky(root, controller.signal, cleanups);
    initAccessories(root, controller.signal);
    initVariantSync(root, cleanups);
    instances.set(root, cleanups);
    console.info(LOG, 'ready', root.dataset.section);
  }

  function destroy(root) {
    const cleanups = instances.get(root);
    if (!cleanups) return;
    cleanups.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        /* ignore */
      }
    });
    instances.delete(root);
  }

  const initAll = () => document.querySelectorAll('.nebula-pdp[data-section]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('.nebula-pdp[data-section]').forEach(init);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('.nebula-pdp[data-section]').forEach(destroy);
  });
})();
